import type {
  LocalizedString,
  LocalizedStringArray,
  DataJsonSection,
  DataJsonOption,
  DefaultOption,
} from '../types';
import { getStoredToken } from '../utils/auth';
import { read_json_body, http_error_message } from '../utils/http';

/**
 * Function-based client for the /api/admin endpoints.
 * All requests carry the stored admin JWT; errors are thrown as Error with
 * a readable message (409s surface the duplicate-username explanation).
 */

export interface ProjectListItem {
  id: string;
  company_name: string;
  created_at: string;
  updated_at: string;
  client_username: string | null;
}

export interface AdminSection extends DataJsonSection {
  sort_order?: number;
}

export interface AdminOption extends DataJsonOption {
  sort_order?: number;
}

export interface AdminDefaultOption extends DefaultOption {
  sort_order?: number;
}

export interface ProjectDetail {
  id: string;
  company_name: string;
  created_at: string;
  updated_at: string;
  client_username: string | null;
  sections: AdminSection[];
  options: AdminOption[];
  default_options: AdminDefaultOption[];
}

export interface SectionInput {
  title: LocalizedString;
  optional: boolean;
  options_num?: number | null;
  sort_order?: number;
}

export interface OptionInput {
  title: LocalizedString;
  description: LocalizedString;
  price?: { us: number; eu?: number; kzt?: number } | null;
  pros?: LocalizedStringArray | null;
  cons?: LocalizedStringArray | null;
  days_to_complete?: number | null;
  is_per_hour?: boolean;
  allow_multiple_instances?: boolean;
  min_instances?: number | null;
  max_instances?: number | null;
  image?: { url: string; title: LocalizedString } | null;
  sort_order?: number;
}

export interface DefaultOptionInput {
  title: LocalizedString;
  description: LocalizedString;
  price?: { us: number; eu?: number; kzt?: number } | null;
  days_to_complete?: number | null;
  sort_order?: number;
}

const request_api = async <T>(
  path: string,
  method: string,
  body?: unknown
): Promise<T> => {
  const token = getStoredToken();

  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? ''}`,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const data = await read_json_body<T & { error?: string }>(response);

  if (!response.ok || data === null) {
    const message =
      (data as { error?: string } | null)?.error ||
      http_error_message(response, data !== null);
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  return data as T;
};

// --- Projects ---

export const listProjects = (): Promise<{ projects: ProjectListItem[] }> =>
  request_api('/api/admin/projects', 'GET');

export const createProject = (input: {
  company_name: string;
  username: string;
  password: string;
}): Promise<{ id: string }> => request_api('/api/admin/projects', 'POST', input);

export const getProjectDetail = (id: string): Promise<ProjectDetail> =>
  request_api(`/api/admin/projects/${id}`, 'GET');

export const renameProject = (id: string, company_name: string): Promise<{ id: string }> =>
  request_api(`/api/admin/projects/${id}`, 'PUT', { company_name });

export const deleteProject = (id: string): Promise<{ success: boolean }> =>
  request_api(`/api/admin/projects/${id}`, 'DELETE');

export const upsertClient = (
  projectId: string,
  input: { username: string; password?: string }
): Promise<{ project_id: string; username: string }> =>
  request_api(`/api/admin/projects/${projectId}/client`, 'PUT', input);

// --- Sections ---

export const createSection = (
  projectId: string,
  input: SectionInput
): Promise<AdminSection> =>
  request_api('/api/admin/sections', 'POST', { project_id: projectId, ...input });

export const updateSection = (id: string, input: SectionInput): Promise<{ id: string }> =>
  request_api(`/api/admin/sections/${id}`, 'PUT', input);

export const deleteSection = (id: string): Promise<{ success: boolean }> =>
  request_api(`/api/admin/sections/${id}`, 'DELETE');

// --- Options ---

export const createOption = (
  projectId: string,
  sectionId: string,
  input: OptionInput
): Promise<{ id: string }> =>
  request_api('/api/admin/options', 'POST', {
    project_id: projectId,
    section_id: sectionId,
    ...input,
  });

export const updateOption = (id: string, input: OptionInput): Promise<{ id: string }> =>
  request_api(`/api/admin/options/${id}`, 'PUT', input);

export const deleteOption = (id: string): Promise<{ success: boolean }> =>
  request_api(`/api/admin/options/${id}`, 'DELETE');

// --- Default options ---

export const createDefaultOption = (
  projectId: string,
  input: DefaultOptionInput
): Promise<{ id: string }> =>
  request_api('/api/admin/default-options', 'POST', {
    project_id: projectId,
    ...input,
  });

export const updateDefaultOption = (
  id: string,
  input: DefaultOptionInput
): Promise<{ id: string }> =>
  request_api(`/api/admin/default-options/${id}`, 'PUT', input);

export const deleteDefaultOption = (id: string): Promise<{ success: boolean }> =>
  request_api(`/api/admin/default-options/${id}`, 'DELETE');

// --- Translation ---

export type TranslatableFieldKind = 'string' | 'array';

export interface TranslatableField {
  key: string;
  kind: TranslatableFieldKind;
  ru: string | string[];
}

export interface TranslatedField {
  en: string | string[];
  kz: string | string[];
}

/** Sends Russian text to /api/admin/translate and gets en/kz back */
export const translateFields = (
  fields: TranslatableField[]
): Promise<{ translations: Record<string, TranslatedField> }> =>
  request_api('/api/admin/translate', 'POST', { fields });

// --- Currency conversion ---

export interface ConvertPriceResult {
  price: { us: number; eu: number; kzt: number };
  rates: { eu: number; kzt: number };
  /** 'fallback' means the FX API was unreachable and hardcoded rates were used */
  source: 'cache' | 'live' | 'fallback';
  fetched_at: string | null;
}

/** Sends a dollar amount to /api/admin/convert-price and gets EUR/KZT back */
export const convertPrice = (us: number): Promise<ConvertPriceResult> =>
  request_api('/api/admin/convert-price', 'POST', { us });
